import React, { useCallback, useMemo, useState } from 'react'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { toast } from 'sonner'
import Button from '../../ui/Button'
import ConfirmDialog from '../../ui/ConfirmDialog'
import AlxModalShell from './AlxModalShell'
import PassMappingPanel from './PassMappingPanel'
import { useWpsSections } from '../../../contexts/WpsSectionsContext'
import { aggregateFromServerResponse } from '../../../utils/weldParameterAggregator'
import { remapPassMappings } from '../../../utils/weldTelemetryClient'
import { validatePassMappings } from '../../../utils/weldUpload/validatePassMappings'
import { usePassMappingAutofill } from '../../../utils/weldUpload/usePassMappingAutofill'
import { findUnmappedWpsPassLabels } from '../../../utils/weldParametersEmptyPasses'
import { DATA_360_DISPLAY_NAME } from '../../../utils/data360DisplayName'
import {
  passMappingKeysFromRows,
  passMappingRowsFromKeys,
  type PassMappingRow
} from '../../../utils/weldUpload/passMappingKeys'
import { applyData360FsTimesToFloorsheet } from '../../../utils/applyData360FsTimesToFloorsheet'

export interface Data360PassMappingDialogProps {
  open: boolean
  onClose: () => void
  projectId: string
  pipeSetId: string
  weldRowId: string
  weldLabel?: string
  /** Torch-aware mapping rows (preferred). */
  mappingRows?: PassMappingRow[]
  /** Legacy: plain zone names or `Lead:HB` keys from persisted import metadata. */
  rawPassNames?: string[]
  wpsPassLabels: string[]
  onComplete: () => void
}

export default function Data360PassMappingDialog({
  open,
  onClose,
  projectId,
  pipeSetId,
  weldRowId,
  weldLabel = '',
  mappingRows,
  rawPassNames = [],
  wpsPassLabels,
  onComplete
}: Data360PassMappingDialogProps) {
  const { getSection, updateSection } = useWpsSections()
  const [busy, setBusy] = useState(false)
  const [unmappedConfirmOpen, setUnmappedConfirmOpen] = useState(false)

  const resolvedMappingRows = useMemo(
    () =>
      mappingRows && mappingRows.length > 0
        ? mappingRows
        : passMappingRowsFromKeys(rawPassNames),
    [mappingRows, rawPassNames]
  )

  const mappingKeys = useMemo(
    () => passMappingKeysFromRows(resolvedMappingRows),
    [resolvedMappingRows]
  )

  const {
    passMappings,
    setPassMappings,
    loading: passMappingsLoading,
    learnedPrefillCount
  } = usePassMappingAutofill({
    passNames: mappingKeys,
    wpsPassLabels,
    heuristicMode: 'telemetry',
    enabled: open
  })

  const validation = useMemo(
    () => validatePassMappings(mappingKeys, passMappings),
    [mappingKeys, passMappings]
  )

  const unmappedWpsLabels = useMemo(
    () => findUnmappedWpsPassLabels(wpsPassLabels, passMappings),
    [wpsPassLabels, passMappings]
  )

  const performSubmit = useCallback(async () => {
    setBusy(true)
    try {
      const remote = await remapPassMappings({
        projectId,
        pipeSetId,
        weldId: weldRowId,
        passMappings
      })
      const aggregate = aggregateFromServerResponse(remote)
      const existing = getSection(`weldparams.${weldRowId}`)
      const existingRecord =
        existing && typeof existing === 'object' ? (existing as Record<string, unknown>) : {}
      const existingData360 =
        existingRecord.data360 && typeof existingRecord.data360 === 'object'
          ? (existingRecord.data360 as Record<string, unknown>)
          : {}
      await updateSection(`weldparams.${weldRowId}`, {
        ...existingRecord,
        source: 'upload',
        aggregate,
        passMappings: remote.passMappings || passMappings,
        data360: {
          ...existingData360,
          passMappingComplete: true
        }
      })
      const passLabels = (aggregate.rows ?? [])
        .map((r) => String(r.pass ?? '').trim())
        .filter(Boolean)
      try {
        const timesResult = await applyData360FsTimesToFloorsheet({
          projectId,
          pipeSetId,
          weldRowId,
          passLabels,
          getSection,
          updateSection
        })
        if (timesResult.applied) {
          toast.success('Pass names mapped. Weld completion times filled from logs.')
        } else {
          toast.success('Pass names mapped successfully.')
        }
      } catch {
        toast.success('Pass names mapped successfully.')
      }
      onComplete()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to apply pass mappings.'
      toast.error(message)
    } finally {
      setBusy(false)
    }
  }, [projectId, pipeSetId, weldRowId, passMappings, getSection, updateSection, onComplete])

  const handleContinueClick = useCallback(() => {
    if (!validation.valid) {
      toast.error(
        `Map all ${DATA_360_DISPLAY_NAME} zone passes before continuing (${validation.unmapped.length} unmapped).`
      )
      return
    }
    if (unmappedWpsLabels.length > 0) {
      setUnmappedConfirmOpen(true)
      return
    }
    void performSubmit()
  }, [validation, unmappedWpsLabels, performSubmit])

  const handleUnmappedConfirm = useCallback(() => {
    setUnmappedConfirmOpen(false)
    void performSubmit()
  }, [performSubmit])

  const continueDisabled =
    !validation.valid || busy || passMappingsLoading
  const continueDisabledReason = passMappingsLoading
    ? 'Loading suggested pass mappings…'
    : !validation.valid
      ? `Map all ${DATA_360_DISPLAY_NAME} zone passes (${validation.unmapped.length} unmapped).`
      : undefined

  const footer = (
    <div className="flex items-center justify-end gap-2">
      <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
        Cancel
      </Button>
      <Button
        type="button"
        data-testid="data360-rename-pass-continue"
        disabled={continueDisabled}
        title={continueDisabledReason}
        onClick={handleContinueClick}
      >
        {busy ? 'Applying…' : 'Continue to Parameters'}
      </Button>
    </div>
  )

  return (
    <>
      <AlxModalShell
        open={open}
        onClose={onClose}
        title="Rename Pass"
        subtitle={`Map each ${DATA_360_DISPLAY_NAME} Lead and Trail zone to a WPS label, then open Parameters.`}
        width="lg"
        testId="data360-rename-pass-dialog"
        closeDisabled={busy}
        footer={footer}
        icon={<ArrowPathIcon className="h-5 w-5" aria-hidden />}
      >
        {weldLabel ? (
          <div className="mb-4 rounded-lg border border-blueprint-blue/20 bg-blueprint-blue/5 px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-heritage-grey">
              Weld ID
            </p>
            <p className="text-base font-semibold text-blueprint-blue" data-testid="data360-rename-weld-id">
              {weldLabel}
            </p>
          </div>
        ) : null}
        <PassMappingPanel
          mappingRows={resolvedMappingRows}
          passMappings={passMappings}
          wpsPassLabels={wpsPassLabels}
          sourceLabel={`${DATA_360_DISPLAY_NAME} zone`}
          loading={passMappingsLoading}
          learnedPrefillCount={learnedPrefillCount}
          onMappingChange={(key, canonical) =>
            setPassMappings((prev) => ({ ...prev, [key]: canonical }))
          }
          disabled={busy}
        />
      </AlxModalShell>
      <ConfirmDialog
        open={unmappedConfirmOpen}
        zIndex={10020}
        title="Some WPS passes are not mapped"
        description={`The following passes are not mapped to any zone pass: ${unmappedWpsLabels.join(', ')}. They will appear empty in Welding Parameters. Continue anyway?`}
        confirmLabel="Continue to Parameters"
        cancelLabel="Go back"
        confirmBusy={busy}
        confirmBusyLabel="Applying…"
        onConfirm={handleUnmappedConfirm}
        onCancel={() => setUnmappedConfirmOpen(false)}
      />
    </>
  )
}
